/**
 * RelayLoadCarousel — the relay load (panel/zone) section of a relay card.
 *
 * The relay's loads (shown as billboard panels or lighting zones) are paged
 * one at a time as a RelayLoadCarouselCard: tap a dot, swipe horizontally, or
 * use the flanking chevrons to move between them. A single load shows just the
 * card; a relay with no loads keeps the card shell with a "no face/zone set up"
 * message and a Setup affordance.
 */
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { Box, Button, ButtonBase, IconButton, Typography } from '@mui/material';
import moment from 'moment-timezone';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectLightFixtures } from '@sophocles/features/reference';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import RelayLoadCarouselCard, { CARD_HEIGHT } from '@sophocles/views/sites/components/RelayCards/RelayLoadCarouselCard';

// Colored "<advertiser> ends/starts/ended …" contract status line for a panel;
// the text color (active / upcoming / expired) conveys the state.
function buildContractString(contract, tz, now, t) {
  const contractEnd =
    contract.contract_end != null ? moment.tz(contract.contract_end + ' 12:00', tz).add(1, 'day') : null;

  const contractStart = contract.contract_start != null ? moment.tz(contract.contract_start + ' 12:00', tz) : null;

  const isActive = contractEnd ? now.isBefore(contractEnd) : true;
  const willStart = contractStart ? now.isBefore(contractStart) : true;

  // Charting can leave the advertiser blank ('' as well as null) — the line
  // still needs a subject, or it reads as a bare "ends in 6 days".
  const advertiser = contract.advertiser?.trim() || t('contracts.contract', 'Contract');

  return (
    <span style={{ color: willStart ? 'var(--status-info)' : isActive ? 'var(--status-ok)' : 'var(--status-warning)' }}>
      {advertiser}{' '}
      {willStart
        ? t('contracts.starts', 'starts')
        : isActive
          ? t('contracts.ends', 'ends')
          : t('contracts.ended', 'ended')}{' '}
      {!contractEnd ? t('contracts.never', 'never') : willStart ? contractStart.fromNow() : contractEnd.fromNow()}
    </span>
  );
}

// Resolve a relay load's `fixture_id` to its display name via the light-fixtures
// store, prefixed with the load's fixture count — e.g. "2× 400W Metal Halide".
function resolveFixtureName(relayLoad, lightFixtures) {
  const fixtureId = relayLoad?.fixture_id ?? null;
  if (fixtureId == null) return null;
  const resolved = lightFixtures.find(f => String(f.id) === String(fixtureId));
  const name = resolved?.name ?? null;
  if (!name) return null;
  const count = relayLoad.number_of_fixtures ?? null;
  return count ? `${count}× ${name}` : name;
}

// Pick the best contract to surface: the earliest current/future one, else the
// most recently finished one.
function pickContract(contracts, siteTimeZone, now) {
  const existingOrFuture = contracts
    .filter(c => now.isSameOrBefore(moment.tz(moment(c.contract_end).add(1, 'day'), siteTimeZone), 'day'))
    .sort((a, b) => moment(a.contract_start).diff(moment(b.contract_start)));
  const finished = contracts
    .filter(c => now.isAfter(c.contract_end, 'day'))
    .sort((a, b) => moment(a.contract_end).diff(moment(b.contract_end)));

  if (existingOrFuture.length > 0) return existingOrFuture[0];
  return finished.length > 0 ? finished[finished.length - 1] : null;
}

function RelayLoadCarousel({
  siteTimeZone,
  loads,
  onPanelClicked,
  addPanelFunction,
  hasCharting,
  showFixtures,
  canSetUp = false,
}) {
  const { t } = useTranslation();
  const relayLoadTerm = useRelayLoadTerm();
  const lightFixtures = useSelector(selectLightFixtures);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollerRef = useRef(null);
  const slideRefs = useRef([]);
  const ticking = useRef(false);

  // Active panel follows the native horizontal scroll position (nearest slide
  // centre), rAF-throttled.
  const updateActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slideRefs.current.forEach((slide, i) => {
      if (!slide) return;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActiveIndex(best);
  }, []);

  const onScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      updateActive();
      ticking.current = false;
    });
  }, [updateActive]);

  // Scroll the inner container only (not scrollIntoView, which could also nudge
  // the page vertically). Chevrons and dots both route through here.
  const goTo = useCallback(index => {
    const el = scrollerRef.current;
    const slides = slideRefs.current;
    const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!el || !slide) return;
    el.scrollTo({ left: slide.offsetLeft - (el.clientWidth - slide.offsetWidth) / 2, behavior: 'smooth' });
  }, []);

  // No loads configured yet: keep the card shell (so the relay card height holds
  // steady) with an empty-state message and a contained Setup affordance.
  if (!loads || loads.length === 0) {
    const emptyLabel = t('relay.noneSetUp', 'No {{term}} set up', { term: relayLoadTerm.singular });
    return (
      <Box sx={{ px: 1.5, py: 0 }}>
        <Box
          sx={{
            height: CARD_HEIGHT,
            borderRadius: 3,
            bgcolor: 'action.hover',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            px: 2,
          }}
        >
          {canSetUp ? (
            <Button variant="contained" color="primary" onClick={addPanelFunction}>
              {t('actions.setup', 'Setup')}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
          )}
        </Box>
        {/* A single placeholder dot in the same indicator row a loaded card
            uses, so a no-load card lines up to the exact same height. */}
        <Box sx={{ my: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          <Box sx={{ height: 6, width: 6, borderRadius: 3, bgcolor: 'action.disabled' }} />
        </Box>
      </Box>
    );
  }

  const now = moment().tz(siteTimeZone);

  const panels = loads.map(load => {
    const contracts = load.contracts ?? [];
    const contract = pickContract(contracts, siteTimeZone, now);

    return {
      key: load.id,
      name: load.name ?? t('general.unknown', 'Unknown'),
      description: load.description ?? '',
      direction: load.direction ?? null,
      fixtureName: showFixtures ? resolveFixtureName(load, lightFixtures) : null,
      advertiser: contract ? (contract.advertiser ?? null) : null,
      contractLine: hasCharting && contract ? buildContractString(contract, siteTimeZone, now, t) : null,
    };
  });

  const panelCount = panels.length;
  const safeIndex = Math.min(activeIndex, panelCount - 1);
  const hasMultiple = panelCount > 1;

  return (
    <Box sx={{ px: 1.5, py: 0 }}>
      {/* Relative frame: the fixed-height card, with the arrows and indicators
          overlaid so a single load and many loads occupy identical height. */}
      <Box sx={{ position: 'relative' }}>
        <Box
          ref={scrollerRef}
          onScroll={onScroll}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {panels.map((panel, index) => (
            <Box
              key={panel.key}
              ref={el => {
                slideRefs.current[index] = el;
              }}
              sx={{ flex: '0 0 100%', scrollSnapAlign: 'center', minWidth: 0 }}
            >
              <RelayLoadCarouselCard
                name={panel.name}
                direction={panel.direction}
                description={panel.description}
                fixtureName={panel.fixtureName}
                contractLine={panel.contractLine}
                onClick={onPanelClicked ?? undefined}
              />
            </Box>
          ))}
        </Box>

        {hasMultiple ? (
          <>
            <IconButton
              size="small"
              onClick={() => goTo(safeIndex - 1)}
              disabled={safeIndex === 0}
              aria-label={t('actions.previous', 'Previous')}
              sx={{ position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary' }}
            >
              <ChevronLeftRounded fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              onClick={() => goTo(safeIndex + 1)}
              disabled={safeIndex === panelCount - 1}
              aria-label={t('actions.next', 'Next')}
              sx={{
                position: 'absolute',
                right: 2,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.secondary',
              }}
            >
              <ChevronRightRounded fontSize="small" />
            </IconButton>
          </>
        ) : null}
      </Box>
      <Box
        sx={{
          my: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
        }}
      >
        {panels.map((panel, index) => (
          <ButtonBase
            key={panel.key}
            onClick={() => goTo(index)}
            aria-label={t('sites.goToPanel', 'Go to {{name}}', { name: panel.name })}
            sx={{
              height: 6,
              width: index === safeIndex ? 16 : 6,
              borderRadius: 3,
              bgcolor: index === safeIndex ? 'primary.main' : 'action.disabled',
              transition:
                'width var(--duration-normal) var(--easing-standard), background-color var(--duration-normal) var(--easing-standard)',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default RelayLoadCarousel;
